import { useCustomHistory } from 'hooks/useHistory'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { schema } from 'prose-core'
import { NodeSelection } from 'prosemirror-state'
import { useContext } from 'react'
import { QueryClientProvider } from 'react-query'
import { Provider } from 'react-redux'
import { Router } from 'react-router-dom'
import store from 'redux/store'
import { AlmanacEditorStateContext } from 'stores/EditorState'
import { TextInlineFormatBarProps } from './Types'
import { ReadOnlyFormatBar } from './components/FormatBars/ReadOnlyFormatBar'
import { TableOfContentsEditModeFormatBar } from './components/FormatBars/TableOfContentsEditModeFormatBar'
import { ErrorBoundary } from '@sentry/react'
import { FormatBar as ImageFormatBar } from 'common/Prose/nodeViews/Image/components/FormatBar/FormatBar'
import { ImageNodeAttrs } from 'common/Prose/nodeViews/Image/Types'
import { EditModeFormatBar } from './components/FormatBars/EditModeFormatBar'

const TextInlineFormatBar = observer((props: TextInlineFormatBarProps) => {
  const history = useCustomHistory()
  const { isMobile } = useResponsiveDesign()

  if (isMobile) return null

  return (
    <QueryClientProvider client={props.queryClient}>
      <Provider store={store}>
        <Router history={history}>
          <FormatBar {...props} />
        </Router>
      </Provider>
    </QueryClientProvider>
  )
})

const FormatBar = observer(function FormatBar(props: TextInlineFormatBarProps) {
  const stateContext = useContext(AlmanacEditorStateContext)
  const { docStore } = props
  if (!stateContext) return null
  const editable = computed(() => stateContext.view.editable).get()
  const selection = computed(() => stateContext.selection).get()

  if (!editable) {
    return <ReadOnlyFormatBar {...props} />
  }

  if (selection instanceof NodeSelection && selection.node.type === schema.nodes.table_of_contents) {
    return <TableOfContentsEditModeFormatBar {...props} />
  }

  if (selection instanceof NodeSelection && selection.node.type === schema.nodes.image) {
    return (
      <ImageFormatBar
        view={props.view}
        getPos={() => selection.from}
        layerId={docStore?.layerId || ''}
        attrs={selection.node.attrs as ImageNodeAttrs}
        showOnlyComment={props.inCommentMode}
      />
    )
  }

  return <EditModeFormatBar {...props} />
})

const WithErrorBoundary = (props: TextInlineFormatBarProps) => (
  <ErrorBoundary>
    <TextInlineFormatBar {...props} />
  </ErrorBoundary>
)

export { WithErrorBoundary as TextInlineFormatBar }
